import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import type {
  Issue, IssueAnnotation, IssueSeverityId, IssueStatusId,
} from '../../screens/issues/issues.types';

/** Body posted to {@code POST /api/v1/issues}. Mirrors the backend
 *  {@code RaiseIssueRequest} record. */
export interface RaiseIssueRequest {
  title: string;
  description: string;
  severity: IssueSeverityId;
  route: string;
  hash: string;
  module: string;
  crumbs: string[];
  viewport: string;
  theme: string;
  browser: string;
  /** Id returned by {@link IssueApiService.uploadAttachment}. Set when
   *  the reporter's overlay captured a PNG — the backend claims the
   *  attachment for this new issue on the server side. */
  snapshotAttachmentId?: string | null;
  annotations?: { kind: 'box' | 'arrow'; coords: Record<string, number> }[];
}

/** Response from {@code POST /api/v1/issues/attachments}. */
export interface AttachmentUploadResponse {
  id: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

/** Wire shape from {@code POST /api/v1/issues}. */
export interface IssueCreatedWire {
  issue: IssueDetailWire;
}

/** Paged envelope returned by the list endpoints (Phase 5). */
export interface PageWire<T> {
  items: T[];
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
}
export interface PagedIssues {
  items: Issue[];
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
}

/** Wire shape from {@code GET /api/v1/issues/{id}} — the detail
 *  endpoint. Uses the FE-side {@link IssueStatusId} / {@link IssueSeverityId}
 *  union labels directly (backend Jackson round-trip via {@code @JsonValue}). */
export interface IssueMessageWire {
  id: string;
  side: 'reporter' | 'tech' | 'system';
  authorId: string | null;
  /** Denormalised author display name — preferred over resolving authorId. */
  authorName: string | null;
  body: string;
  createdAt: string;
}
export interface IssueEventWire {
  id: string;
  label: string;
  byName: string | null;
  at: string;
}
export interface IssueAnnotationWire {
  id: string;
  kind: 'box' | 'arrow';
  coords: Record<string, number>;
  ordinal: number;
}
export interface IssueSummaryWire {
  id: string;
  code: string;
  title: string;
  severity: IssueSeverityId;
  status: IssueStatusId;
  rounds: number;
  reporterId: string;
  assigneeId: string | null;
  /** Denormalised assignee display name (from the issue-desk directory) so the
   *  owner renders without resolving the id against a directory. */
  assigneeName: string | null;
  route: string;
  hash: string;
  module: string;
  crumbs: string[];
  viewport: string;
  theme: string;
  browser: string;
  /** Presigned GET URL for the snapshot PNG. Null when the issue was
   *  filed without a snapshot. TTL {@code minio.presigned-ttl-minutes}
   *  (60m default) — a fresh URL rides on every read. */
  snapshotUrl: string | null;
  createdAt: string;
  updatedAt: string;
}
/** Slim issue-desk directory entry (holders of {@code issue.triage} from
 *  identity-service) that feeds the assignee dropdown. */
export interface IssueDeskUser {
  id: string;
  name: string;
}
export interface IssueDetailWire extends IssueSummaryWire {
  description: string;
  reporterAgencyId: string | null;
  messages: IssueMessageWire[];
  events: IssueEventWire[];
  annotations: IssueAnnotationWire[];
}

/** REST client for the issue-service. Reads swallow errors to keep
 *  the FE working against the seed mock when the backend is down;
 *  writes bubble errors up so the store can toast the user. */
@Injectable({ providedIn: 'root' })
export class IssueApiService {
  private readonly http = inject(HttpClient);

  /** Relative, same-origin base URL — like every other service client.
   *
   *  Works in all three deployment shapes, which is why it is relative rather
   *  than absolute:
   *    · `ng serve`      → src/proxy.conf.json forwards to issue-service :8160
   *    · manual VM build → the B2B nginx forwards the same path to issue-service
   *    · CI/CD prod      → gatewayUrlInterceptor rewrites it to
   *                        {apiBaseUrl}/issue-service/api/v1/issues
   *
   *  It also avoids CORS on issue writes when the console is opened from
   *  b2b.hilda-dev.ir. (Was hardcoded to http://localhost:8160, which broke on
   *  the server.) */
  private readonly baseUrl = '/api/v1/issues';

  /** Tracker queue — paginated.  {@code updatedSince} feeds the
   *  30-second incremental poll: callers stash the max {@code updatedAt}
   *  they've seen and pass it back to only receive the delta. Errors
   *  resolve to an empty page so the FE keeps rendering the mock seed. */
  queue(params: {
    status?: string; severity?: string; assignee?: string; q?: string;
    updatedSince?: Date | null;
    /** {@code 'urgency'} sorts by the FE weight formula projected into
     *  SQL (Phase 5.5). Anything else — including undefined — leaves
     *  the backend on its default {@code updatedAt DESC} which is what
     *  the 30s delta poll needs. */
    sort?: 'urgency' | 'updatedAt';
    page?: number; size?: number;
  } = {}): Observable<PagedIssues> {
    let p = new HttpParams();
    if (params.status)         p = p.set('status',       params.status);
    if (params.severity)       p = p.set('severity',     params.severity);
    if (params.assignee)       p = p.set('assignee',     params.assignee);
    if (params.q)              p = p.set('q',            params.q);
    if (params.updatedSince)   p = p.set('updatedSince', params.updatedSince.toISOString());
    if (params.sort)           p = p.set('sort',         params.sort);
    if (params.page   != null) p = p.set('page',         String(params.page));
    if (params.size   != null) p = p.set('size',         String(params.size));
    return this.http.get<PageWire<IssueSummaryWire>>(this.baseUrl, { params: p }).pipe(
      map((page) => ({ ...page, items: page.items.map(summaryToIssue) })),
      catchError(() => of(emptyPage())),
    );
  }

  mine(params: { updatedSince?: Date | null; page?: number; size?: number } = {}): Observable<PagedIssues> {
    let p = new HttpParams();
    if (params.updatedSince)   p = p.set('updatedSince', params.updatedSince.toISOString());
    if (params.page   != null) p = p.set('page',         String(params.page));
    if (params.size   != null) p = p.set('size',         String(params.size));
    return this.http.get<PageWire<IssueSummaryWire>>(`${this.baseUrl}/mine`, { params: p }).pipe(
      map((page) => ({ ...page, items: page.items.map(summaryToIssue) })),
      catchError(() => of(emptyPage())),
    );
  }

  get(id: string): Observable<Issue | null> {
    return this.http.get<IssueDetailWire>(`${this.baseUrl}/${id}`).pipe(
      map(detailToIssue),
      catchError(() => of(null)),
    );
  }

  // ── write path ─────────────────────────────────────────────────

  /** POST /issues — quote {@code snapshotAttachmentId} in the body if
   *  the reporter's overlay already uploaded a PNG via
   *  {@link uploadAttachment}. Server claims the attachment for the
   *  new issue on its side. */
  create(req: RaiseIssueRequest): Observable<Issue> {
    return this.http.post<IssueCreatedWire>(this.baseUrl, req).pipe(
      map((wire) => detailToIssue(wire.issue)),
    );
  }

  /** POST /issues/attachments — multipart. Kick this the moment the
   *  overlay's {@code html-to-image} capture returns a Blob so the
   *  reporter isn't waiting on network I/O at submit time. Returns
   *  the id to quote on the create-issue body. */
  uploadAttachment(blob: Blob, filename = 'snapshot.png'): Observable<AttachmentUploadResponse> {
    const form = new FormData();
    // Some browsers stamp "application/octet-stream" on a raw Blob;
    // rewrap so the server sees a real content type it can validate.
    const typed = blob.type ? blob : new Blob([blob], { type: 'image/png' });
    form.append('file', typed, filename);
    return this.http.post<AttachmentUploadResponse>(`${this.baseUrl}/attachments`, form);
  }

  postMessage(codeOrId: string, body: string): Observable<Issue> {
    return this.http.post<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/messages`, { body })
      .pipe(map(detailToIssue));
  }
  setStatus(codeOrId: string, status: IssueStatusId): Observable<Issue> {
    return this.http.patch<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/status`, { status })
      .pipe(map(detailToIssue));
  }
  setAssignee(codeOrId: string, assigneeId: string | null, assigneeName?: string | null): Observable<Issue> {
    // Send the picked display name so the backend denormalises it onto the issue
    // (the reporter side then renders the owner without a directory lookup).
    return this.http.patch<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/assignee`,
        { assigneeId: assigneeId ?? null, assigneeName: assigneeName ?? null })
      .pipe(map(detailToIssue));
  }

  /** Issue-desk directory (real Developers + Technical Leaders from
   *  identity-service) for the assignee dropdown. Swallows errors to an empty
   *  list so the tracker still works (dropdown falls back to just Unassigned). */
  directory(): Observable<IssueDeskUser[]> {
    return this.http.get<IssueDeskUser[]>(`${this.baseUrl}/directory`).pipe(
      catchError(() => of([] as IssueDeskUser[])),
    );
  }
  setSeverity(codeOrId: string, severity: IssueSeverityId): Observable<Issue> {
    return this.http.patch<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/severity`, { severity })
      .pipe(map(detailToIssue));
  }
  approve(codeOrId: string, text?: string): Observable<Issue> {
    return this.http.post<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/approve`,
        text ? { text } : {})
      .pipe(map(detailToIssue));
  }
  reject(codeOrId: string, text: string): Observable<Issue> {
    return this.http.post<IssueDetailWire>(`${this.baseUrl}/${codeOrId}/reject`, { text })
      .pipe(map(detailToIssue));
  }
}

/** ── wire → FE Issue mappers ─────────────────────────────────────── */

/** Sentinel returned by the queue/mine catchError branches so the FE
 *  can distinguish "backend errored" from "backend returned zero rows". */
function emptyPage(): PagedIssues {
  return { items: [], page: 0, size: 0, total: 0, hasMore: false };
}

/** Parse the viewport string ("1440 × 900" or "1440 x 900") into a
 *  {w,h} pair so the snapshot renderer scales correctly. Falls back
 *  to a generous 1440x900 when the pattern doesn't match. */
function parseViewport(vp: string): { w: number; h: number } {
  const m = /(\d+)\s*[×x]\s*(\d+)/.exec(vp || '');
  return m
    ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) }
    : { w: 1440, h: 900 };
}

function summaryToIssue(w: IssueSummaryWire): Issue {
  const dim = parseViewport(w.viewport);
  return {
    id: w.code,                // FE keys off ISS-nnnn — matches the mock.
    title: w.title,
    description: '',           // filled in when the detail endpoint is hit.
    severity: w.severity,
    status: w.status,
    rounds: w.rounds,
    reporterId: w.reporterId,
    assignee: w.assigneeId,
    assigneeName: w.assigneeName ?? undefined,
    location: { route: w.route, crumbs: w.crumbs, hash: w.hash, module: w.module },
    env: { viewport: w.viewport, theme: w.theme, browser: w.browser },
    createdAt: new Date(w.createdAt),
    updatedAt: new Date(w.updatedAt),
    // Snapshot renderer reads {@code dataUrl} as its {@code <img [src]>}
    // — a presigned MinIO GET URL works interchangeably with a data URL.
    // Width / height come from the reporter's viewport so the aspect
    // ratio matches what they captured.
    snapshot: w.snapshotUrl
      ? { dataUrl: w.snapshotUrl, w: dim.w, h: dim.h, scrollY: 0 }
      : null,
    annotations: [],           // Detail endpoint populates.
    events: [],
    messages: [],
  };
}
function detailToIssue(w: IssueDetailWire): Issue {
  const base = summaryToIssue(w);
  return {
    ...base,
    description: w.description,
    annotations: w.annotations.map(annotationToFe),
    events: w.events.map((e) => ({
      t: new Date(e.at), label: e.label, by: e.byName ?? '—',
    })),
    messages: w.messages.map((m) => ({
      id: m.id, side: m.side, author: m.authorId ?? undefined,
      authorName: m.authorName ?? undefined,
      text: m.body, at: new Date(m.createdAt),
    })),
  };
}
function annotationToFe(a: IssueAnnotationWire): IssueAnnotation {
  const c = a.coords;
  if (a.kind === 'box') {
    return { type: 'box', x: c['x'] ?? 0, y: c['y'] ?? 0, w: c['w'] ?? 0, h: c['h'] ?? 0 };
  }
  return { type: 'arrow', x1: c['x1'] ?? 0, y1: c['y1'] ?? 0, x2: c['x2'] ?? 0, y2: c['y2'] ?? 0 };
}
